import isEmpty from 'lodash/isEmpty';
import { SET_VP_EDIT, SET_VP_TYPES, SET_SELECTED_MANAGE_VERSION, SET_VS_PACKAGES } from '../constant/action';
import { LOCAL_VP_EDIT } from '../constant/text';
import { setLocal } from '../api/localStorageUtil';

// BAS-13532: the backend returns raw localized labels (e.g. "12-18 years",
// "6-11 år", "12–18 Jahre") in the `type` field for child age cohorts. The
// frontend translations were updated to "till och med"/"up to and including"
// wording, and 12_18_YEARS/6_12_MONTHS were renamed to 12_17_YEARS/6_11_MONTHS,
// but the production backend cannot be updated. Detect the cohort by numeric
// range + unit word (years/months in any of en/sv/no/de/nl/zh) and rewrite the
// label to a translation key so the Dropdown's t(label) lookup resolves to the
// new wording. Pattern-based instead of an exact-string map so it tolerates
// en-dash vs hyphen, trailing whitespace, and minor word-form variation
// ("Jahre" vs "Jahr", "måneder" vs "måned", etc.).
const YEARS_WORD = /year|år|jahr|jaar|岁/i;
const MONTHS_WORD = /month|månad|måned|monat|maand|个月/i;

// BAS-13532: the backend `vptypeName` field carries the *old* cohort key
// identifiers (`6_12_MONTHS`, `12_18_YEARS`). When the heading wording changed to
// "till och med" the upper bounds dropped (12 -> 11 months, 18 -> 17 years) and
// the translation keys were renamed to `6_11_MONTHS` / `12_17_YEARS`. The backend
// cannot be updated, so map the legacy key to the current translation key before
// the t() lookup. Unlike the dropdown (which receives localized labels and uses
// classifyLegacyVpType), the card/confirm/history titles pass `vptypeName`
// straight into t(); without this they fall back to printing the raw key once the
// renamed-away old keys are gone from the synced translations.
const LEGACY_VPTYPE_NAME_ALIASES = {
    '6_12_MONTHS': '6_11_MONTHS',
    '12_18_YEARS': '12_17_YEARS'
};

export const resolveVpTypeNameKey = vptypeName => LEGACY_VPTYPE_NAME_ALIASES[vptypeName] || vptypeName;

const classifyLegacyVpType = type => {
    if (typeof type !== 'string') {
        return null;
    }
    const t = type.replace(/[–—]/g, '-').trim();
    const hasYears = YEARS_WORD.test(t);
    const hasMonths = MONTHS_WORD.test(t);
    const range = t.match(/^(\d+)\s*-\s*(\d+)/);
    if (range) {
        const low = range[1];
        const high = range[2];
        if (hasMonths) {
            if (low === '0' && high === '2') {
                return '0_2_MONTHS';
            }
            if (low === '3' && high === '5') {
                return '3_5_MONTHS';
            }
            if (low === '6' && (high === '11' || high === '12')) {
                return '6_11_MONTHS';
            }
        }
        if (hasYears) {
            if (low === '3' && high === '5') {
                return '3_5_YEARS';
            }
            if (low === '6' && high === '11') {
                return '6_11_YEARS';
            }
            if (low === '12' && (high === '17' || high === '18')) {
                return '12_17_YEARS';
            }
        }

        return null;
    }
    const single = t.match(/^(\d+)\b/);
    if (single && hasYears) {
        if (single[1] === '1') {
            return '1_YEAR';
        }
        if (single[1] === '2') {
            return '2_YEARS';
        }
    }

    return null;
};

export const vpAction = {
    [SET_VP_TYPES]: (state, payload = []) => {
        if (!state.vp.vpTypes) {
            state.vp.vpTypes = [];
        }

        if (isEmpty(payload)) {
            return {
                vp: {
                    ...state.vp
                }
            };
        }

        payload = payload.map(({ id, type }) => ({ label: classifyLegacyVpType(type) || type, value: id }));

        return {
            vp: {
                ...state.vp,
                vpTypes: payload
            }
        };
    },
    [SET_VS_PACKAGES]: (state, payload) => ({
            manageVersions: { ...state.manageVersions, vp: payload }
        }),
    [SET_SELECTED_MANAGE_VERSION]: (state, payload = []) => ({
            manageVersions: {
                ...state.manageVersions,
                editVersion: {
                    ...state.manageVersions.editVersion,
                    selectedVersion: payload.selectedVersion,
                    selectedVp: payload.selectedVp
                }
            }
        }),
    [SET_VP_EDIT]: (state, payload) => {
        setLocal(LOCAL_VP_EDIT, payload, true);

        return {
            manageVersions: {
                ...state.manageVersions,
                vpEdit: payload
            }
        };
    },
};
